import { useState } from 'react';
import { Box, SimpleGrid, Stack, Group, Badge, Avatar, TextInput, Select } from '@mantine/core';
import {
  IconSearch,
  IconMail,
  IconPhone,
  IconMapPin,
} from '@tabler/icons-react';
import { Card, Text, Title, Button } from '../../../../components/ui';
import { mockUsers, mockProjects, mockTasks } from '../../mockData';

export default function TeamPage() {
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string | null>(null);

  const departments = [...new Set(mockUsers.map(u => u.department))];

  // Get team members who are involved in projects
  const teamMemberIds = new Set(mockProjects.flatMap(p => [p.ownerId, ...p.memberIds]));
  const teamMembers = mockUsers.filter(u => teamMemberIds.has(u.id));

  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(search.toLowerCase()) ||
                          member.email.toLowerCase().includes(search.toLowerCase()) ||
                          member.title.toLowerCase().includes(search.toLowerCase());
    const matchesDepartment = !departmentFilter || member.department === departmentFilter;
    return matchesSearch && matchesDepartment;
  });

  // Calculate member stats
  const getMemberStats = (memberId: string) => {
    const projectCount = mockProjects.filter(p => p.ownerId === memberId || p.memberIds.includes(memberId)).length;
    const taskCount = mockTasks.filter(t => t.assigneeId === memberId).length;
    const completedTasks = mockTasks.filter(t => t.assigneeId === memberId && t.status === 'done').length;
    return { projectCount, taskCount, completedTasks };
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Team</Title>
          <Text c="dimmed" size="sm">{filteredMembers.length} team members</Text>
        </Box>
        <Button>Invite Member</Button>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search team members..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Department"
            data={departments.map(d => ({ value: d, label: d }))}
            value={departmentFilter}
            onChange={setDepartmentFilter}
            clearable
            w={180}
          />
        </Group>
      </Card>

      {/* Team Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        {filteredMembers.map(member => {
          const stats = getMemberStats(member.id);
          return (
            <Card key={member.id}>
              <Stack gap="md">
                <Group>
                  <Avatar size="lg" radius="xl" color="blue">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </Avatar>
                  <Box style={{ flex: 1 }}>
                    <Text fw={600}>{member.name}</Text>
                    <Text size="sm" c="dimmed">{member.title}</Text>
                  </Box>
                  <Badge
                    size="sm"
                    color={member.status === 'active' ? 'green' : member.status === 'busy' ? 'red' : member.status === 'away' ? 'yellow' : 'gray'}
                  >
                    {member.status}
                  </Badge>
                </Group>

                <Stack gap={4}>
                  <Group gap="xs">
                    <IconMail size={14} color="var(--mantine-color-dimmed)" />
                    <Text size="xs" c="dimmed">{member.email}</Text>
                  </Group>
                  {member.phone && (
                    <Group gap="xs">
                      <IconPhone size={14} color="var(--mantine-color-dimmed)" />
                      <Text size="xs" c="dimmed">{member.phone}</Text>
                    </Group>
                  )}
                  {member.location && (
                    <Group gap="xs">
                      <IconMapPin size={14} color="var(--mantine-color-dimmed)" />
                      <Text size="xs" c="dimmed">{member.location}</Text>
                    </Group>
                  )}
                </Stack>

                <Group gap="xs">
                  <Badge size="sm" variant="light">{member.department}</Badge>
                  <Badge size="sm" variant="outline">{member.role}</Badge>
                </Group>

                <Group grow>
                  <Box ta="center" p="xs" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
                    <Text size="lg" fw={700}>{stats.projectCount}</Text>
                    <Text size="xs" c="dimmed">Projects</Text>
                  </Box>
                  <Box ta="center" p="xs" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
                    <Text size="lg" fw={700}>{stats.taskCount}</Text>
                    <Text size="xs" c="dimmed">Tasks</Text>
                  </Box>
                  <Box ta="center" p="xs" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
                    <Text size="lg" fw={700}>{stats.completedTasks}</Text>
                    <Text size="xs" c="dimmed">Completed</Text>
                  </Box>
                </Group>
              </Stack>
            </Card>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}
